import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { typeOf } from '@ember/utils';
import Component from '@glimmer/component';
import { task, dropTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import AutoSignOutTime from 'garaje/components/location-settings/sign-out/auto-sign-out-time';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import PageSection from 'garaje/components/ui/page/section';
import type Config from 'garaje/models/config';
import type Location from 'garaje/models/location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { formatMinutesAsTime } from 'garaje/utils/format-minutes-as-time';

interface SignOutSignature {
  Args: {
    currentLocation: Location;
    config: Config;
  };
}

export default class SignOut extends Component<SignOutSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  toggleSelfSignOut = dropTask(async (value: boolean) => {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_SELF_SIGN_OUT_TOGGLED', { toggledSetting: value });
    const { config } = this.args;
    config.selfSignOut = value;

    try {
      await config.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      config.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  saveTask = task(async () => {
    const { currentLocation } = this.args;
    if (<boolean>(<unknown>currentLocation.hasDirtyAttributes) === false) {
      return;
    }
    const autoSignOutAtMinutesSinceMidnight = <
      [Location['autoSignOutAtMinutesSinceMidnight'], Location['autoSignOutAtMinutesSinceMidnight']]
    >currentLocation.changedAttributes()['autoSignOutAtMinutesSinceMidnight'];
    const [original, changed] = autoSignOutAtMinutesSinceMidnight;
    let action = original === null ? 'Enabled' : 'Edited';
    action = changed === null ? 'Disabled' : action;
    try {
      await currentLocation.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      this.#trackAutoSignOutEvent(action, currentLocation.id, autoSignOutAtMinutesSinceMidnight[1]);
    } catch (e) {
      currentLocation.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  @action
  updateAutoSignOutAtMinutesSinceMidnight(mins: number | null): void {
    const { currentLocation } = this.args;
    currentLocation.autoSignOutAtMinutesSinceMidnight = mins;
    currentLocation.autoSignOutAtMidnight = typeOf(mins) === 'number';
  }

  #trackAutoSignOutEvent(action: string, locationId: string, autoSignOutAtMinutesSinceMidnight: number | null): void {
    this.metrics.trackEvent(`Auto Sign Out ${action}`, {
      location_id: locationId,
      sign_out_at:
        autoSignOutAtMinutesSinceMidnight === null ? null : formatMinutesAsTime(autoSignOutAtMinutesSinceMidnight),
    });
  }

  <template>
    <PageSection>
      <h3>Visitor &amp; employee sign out</h3>
      <form class="space-y-8">
        <fieldset>
          <div>
            <SettingsPanel data-test-self-sign-out as |panel|>
              <panel.header
                @iconUri="/assets/images/features/signout.svg"
                @title="Self sign-out"
                @description="Allow employees and visitors to sign themselves out."
                as |header|
              >
                <header.controls>
                  <label class="toggle">
                    <input
                      data-test-self-sign-out-toggle
                      checked={{@config.selfSignOut}}
                      disabled={{this.toggleSelfSignOut.isRunning}}
                      type="checkbox"
                      {{on "change" (perform this.toggleSelfSignOut value="target.checked")}}
                    />
                    <span></span>
                  </label>
                </header.controls>
              </panel.header>
            </SettingsPanel>
          </div>

          <div>
            <AutoSignOutTime
              @saveTask={{this.saveTask}}
              @updateAutoSignOutAtMinutesSinceMidnight={{this.updateAutoSignOutAtMinutesSinceMidnight}}
              @location={{@currentLocation}}
            />
          </div>
        </fieldset>
      </form>

      <p class="text-sm mb-0">
        Learn more about
        <a
          href="https://help.envoy.com/how-to-sign-out-visitors/"
          target="_blank"
          tabindex="-1"
          rel="noopener"
        >sign-out</a>.
      </p>
    </PageSection>
  </template>
}
